import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { AssetsTab } from "@/components/features/ClientDetails/AssetsTab/AssetsTab";
import { BrandsTab } from "@/components/features/ClientDetails/BrandsTab/BrandsTab";
import { CheckInCheckOutTab } from "@/components/features/ClientDetails/CheckInCheckOutTab/CheckInCheckOutTab";
import { ContactsTab } from "@/components/features/ClientDetails/ContactsTab/ContactsTab";
import { CoverageAreaTab } from "@/components/features/ClientDetails/CoverageAreaTab/CoverageAreaTab";
import { LocationsTab } from "@/components/features/ClientDetails/LocationsTab/LocationsTab";
import BulkImportModal from "@/components/ui/BulkImport/BulkImportModal";
import { locationImportDescriptor } from "@/components/features/ClientDetails/LocationsTab/BulkUpload/locationImportDescriptor";
import { contactImportDescriptor } from "@/components/features/ClientDetails/ContactsTab/BulkUpload/contactImportDescriptor";
import { assetImportDescriptor } from "@/components/features/ClientDetails/AssetsTab/BulkUpload/assetImportDescriptor";
import { brandImportDescriptor } from "@/components/features/Clients/BulkUpload/brandImportDescriptor";
import { NotesTab } from "@/components/features/ClientDetails/NotesTab/NotesTab";
import { PaymentTab } from "@/components/features/ClientDetails/PaymentTab/PaymentTab";
import { RatesTab } from "@/components/features/ClientDetails/RatesTab/RatesTab";
import { WorkOrdersTab } from "@/components/features/ClientDetails/WorkOrdersTab/WorkOrdersTab";
import { ClientHeader } from "@/components/features/ClientHeader/ClientHeader";
import BrandsSearchModal from "@/components/features/ClientOnboarding/Brands/BrandsSearchModal/BrandsSearchModal";
import AddOrEditLocationsModal from "@/components/features/ClientOnboarding/LocationInfo/AddOrEditLocations/AddOrEditLocationsModal";
import { ClientDetailSkeleton } from "@/components/ui/Skeleton";
import { useClientHeader } from "@/hooks/clients/useClientHeader";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { parseStoredCalendarDate } from "@/utils/dateFormatter";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { Flex, Tabs } from "@mantine/core";
import { TabsScrollArea } from "@/components/ui/Tab/TabsScrollArea";
import { useDisclosure } from "@mantine/hooks";
import { createFileRoute, useParams, useSearch } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
// import type { ClientColumnType } from ".";
import { locationQueryKeys } from "@/api/clients/locations";
import { ClientDocumentsTab } from "@/components/features/FileManager/mounts/ClientDocumentsTab";
import { ClientDocuments as LegacyClientDocuments } from "@/components/features/ClientDetails/DocumentsTab/ClientDocuments";

// A19 (MVP-hidden, restore pending product confirm): the legacy Documents tab was
// replaced by the shared FileManager mount (740c19a2); the legacy component is
// preserved per the MVP hide-don't-delete rule. Flip to true to render the old tab.
const SHOW_LEGACY_CLIENT_DOCUMENTS_TAB = false;
import { OverviewTab } from "@/components/features/ClientDetails/OverviewTab/OverviewTab";
import AssetsModal from "@/components/features/ClientOnboarding/AssetsInfo/AssetsModal/AssetsModal";
import ContactsModal from "@/components/features/ClientOnboarding/Contacts/ContactsModal/ContactsModal";
import { EquipmentFeeModal } from "@/components/features/ClientOnboarding/EquipmentFee/EquipmentFeeModal";
import { BrandsQueryKeys } from "@/hooks/clients/brands/useBrandsQueries";
import { z } from "zod";
import styles from "./clientDetail.module.scss";

// Extend the base table search schema to preserve the `tab` param that
// usePersistedTabs writes to the URL alongside table filter params.
const clientDetailSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/clients/$id")({
  component: ClientDetail,
  validateSearch: clientDetailSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

type TabItemType = {
  value: string;
  label: string;
  component: ReactNode;
};

function ClientDetail() {
  const { id } = useParams({ from: "/_authenticated/clients/$id" });
  const searchParams = useSearch({ from: "/_authenticated/clients/$id" });
  const { activeTab, setActiveTab } = usePersistedTabs({ defaultTab: "overview" });
  const [editModeTab, setEditModeTab] = useState<string | null>(null);
  const [locationModalOpened, locationModalHandlers] = useDisclosure(false);
  const [bulkLocationModalOpened, bulkLocationModalHandlers] = useDisclosure(false);
  const [brandsModalOpened, brandsModalHandlers] = useDisclosure(false);
  const [bulkBrandModalOpened, bulkBrandModalHandlers] = useDisclosure(false);
  const [assetsModalOpened, assetsModalHandlers] = useDisclosure(false);
  const [contactsModalOpened, contactsModalHandlers] = useDisclosure(false);
  const [bulkContactModalOpened, bulkContactModalHandlers] = useDisclosure(false);
  const [bulkAssetModalOpened, bulkAssetModalHandlers] = useDisclosure(false);
  const [equipmentModalOpened, equipmentModalHandlers] = useDisclosure(false);
  // Fetch client header data from API (for header and breadcrumb)
  const { data: clientHeaderData, isLoading: isClientHeaderLoading } = useClientHeader(id);

  // Set breadcrumbs - memoize to prevent infinite loops
  const clientName = clientHeaderData?.businessName;
  const hasValidName = clientName && !isClientHeaderLoading;

  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Clients",
        href: "/clients",
        icon: getRouteIcon(MODULE_KEYS_MAP.clients, 16),
      },
      {
        title: hasValidName ? clientName : "Loading...",
        href: `/clients/${id}`,
      },
    ],
    [hasValidName, clientName, id],
  );
  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  // Setup form
  const methods = useForm({
    defaultValues: {},
    mode: "all",
  });

  // Cancel edit mode when switching tabs
  // This hook must be called before any early returns to follow Rules of Hooks
  useEffect(() => {
    if (editModeTab && editModeTab !== activeTab) {
      setEditModeTab(null);
    }
  }, [activeTab, editModeTab]);

  // Define tab configuration
  // This hook must be called before any early returns to follow Rules of Hooks
  const tabsData: TabItemType[] = useMemo(
    () => [
      {
        value: "overview",
        label: "Overview",
        component: <OverviewTab />,
      },
      {
        value: "brands",
        label: "Brands",
        component: <BrandsTab isActive={activeTab === "brands"} searchParams={searchParams} />,
      },
      {
        value: "locations",
        label: "Locations",
        component: (
          <LocationsTab isActive={activeTab === "locations"} searchParams={searchParams} />
        ),
      },
      {
        value: "assets",
        label: "Assets",
        component: <AssetsTab />,
      },
      {
        value: "contacts",
        label: "Contacts",
        component: <ContactsTab />,
      },
      {
        value: "work-orders",
        label: "Work Orders",
        component: (
          <WorkOrdersTab isActive={activeTab === "work-orders"} searchParams={searchParams} />
        ),
      },
      {
        value: "coverage-area",
        label: "Coverage Area",
        component: <CoverageAreaTab />,
      },
      {
        value: "rates",
        label: "Rates",
        component: <RatesTab clientId={id} />,
      },
      {
        value: "payment-invoices",
        label: "Payments & Invoices",
        component: <PaymentTab />,
      },
      {
        value: "documents",
        label: "Documents",
        component: SHOW_LEGACY_CLIENT_DOCUMENTS_TAB ? (
          <LegacyClientDocuments />
        ) : (
          <ClientDocumentsTab />
        ),
      },
      {
        value: "notes",
        label: "Notes",
        component: <NotesTab />,
      },
      {
        value: "checkins-checkouts",
        label: "Check-in/out Instructions",
        component: <CheckInCheckOutTab />,
      },
    ],
    [activeTab, searchParams],
  );

  // Render function for tabs
  const renderTabs = (tabsData: TabItemType[]) => {
    return (
      <>
        <TabsScrollArea>
          <Tabs.List className={styles.tabsList}>
            {tabsData.map((tab) => (
              <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </TabsScrollArea>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {activeTab === tab.value ? tab.component : null}
          </Tabs.Panel>
        ))}
      </>
    );
  };

  if (isClientHeaderLoading) {
    return <ClientDetailSkeleton />;
  }

  return (
    <>
      <Flex
        direction="column"
        style={{
          margin: "calc(var(--mantine-spacing-md) * -1)",
          backgroundColor: "var(--neutral-500)",
          minHeight: "initial",
        }}
        data-slot={"client-detail-container"}
      >
        {/* Client Header */}
        <ClientHeader
          name={clientHeaderData?.businessName || "Client Name"}
          entityName={clientHeaderData?.entityName}
          subtitle={clientHeaderData?.legalName}
          clientType={
            clientHeaderData?.clientType?.label === "Primary"
              ? "Direct Client"
              : clientHeaderData?.clientType?.label === "Secondary"
                ? "Indirect Client"
                : clientHeaderData?.clientType?.label || "Direct Client"
          }
          industry={clientHeaderData?.clientIndustry?.label || "Retail"}
          website={clientHeaderData?.website}
          // SERV-2969: a @db.Date, so read its stored day, not UTC midnight in local time.
          clientSince={parseStoredCalendarDate(clientHeaderData?.clientSince)?.toLocaleDateString(
            "en-US",
            { day: "2-digit", month: "short", year: "numeric" },
          )}
          locations={clientHeaderData?.keyFigures?.locationsCount}
          contacts={clientHeaderData?.keyFigures?.contactsCount}
          assets={clientHeaderData?.keyFigures?.assetsCount}
          brands={clientHeaderData?.keyFigures?.brandsCount}
          activeWorkOrders={clientHeaderData?.keyFigures?.activeWorkOrdersCount}
          keyContacts={clientHeaderData?.keyContacts?.map((c) => ({
            name: [c.firstName, c.lastName].filter(Boolean).join(" ").trim() || "—",
          }))}
          isLoading={isClientHeaderLoading}
          activeTab={activeTab}
          onAddNewClick={(value) => {
            if (value === "add-location") {
              locationModalHandlers.open();
            }
            if (value === "bulk-upload-locations") {
              bulkLocationModalHandlers.open();
            }
            if (value === "add-brand") {
              brandsModalHandlers.open();
            }
            if (value === "bulk-upload-brands") {
              bulkBrandModalHandlers.open();
            }
            if (value === "add-asset") {
              assetsModalHandlers.open();
            }
            if (value === "bulk-upload-assets") {
              bulkAssetModalHandlers.open();
            }
            if (value === "add-contact") {
              contactsModalHandlers.open();
            }
            if (value === "bulk-upload-contacts") {
              bulkContactModalHandlers.open();
            }
            if (value === "add-equipment") {
              equipmentModalHandlers.open();
            }
          }}
        />
        {/* Tabs Container */}
        <FormProvider {...methods}>
          <div className={styles.tabsContainer}>
            <Tabs value={activeTab} onChange={setActiveTab} className={styles.tabs}>
              {renderTabs(tabsData)}
            </Tabs>
          </div>
        </FormProvider>
      </Flex>
      {/* Modals */}
      <AddOrEditLocationsModal
        openModal={locationModalOpened}
        setOpenModal={locationModalHandlers.close}
        editingLocationId={null}
        clientId={id}
        //Intentional prefix invalidation: since this query key is only used for table data, we can safely invalidate the entire list
        queryKeysToInvalidate={[
          BrandsQueryKeys.brandsDetailsTabData,
          locationQueryKeys.getAllLocations,
        ]}
      />
      <BulkImportModal
        descriptor={locationImportDescriptor}
        ctx={{ clientId: id }}
        opened={bulkLocationModalOpened}
        onClose={bulkLocationModalHandlers.close}
      />
      <BrandsSearchModal
        openModal={brandsModalOpened}
        setOpenModal={brandsModalHandlers.close}
        clientId={id}
        //Intentional prefix invalidation: since this query key is only used for table data, we can safely invalidate the entire list
        queryKeysToInvalidate={[
          BrandsQueryKeys.brandsDetailsTabData,
          locationQueryKeys.getAllLocations,
        ]}
      />
      <AssetsModal
        openModal={assetsModalOpened}
        setOpenModal={(open) => {
          const next = typeof open === "function" ? open(assetsModalOpened) : open;
          if (next) assetsModalHandlers.open();
          else assetsModalHandlers.close();
        }}
        editingAsset={null}
        clientId={id}
      />
      <ContactsModal
        openModal={contactsModalOpened}
        setOpenModal={(open) => {
          const next = typeof open === "function" ? open(contactsModalOpened) : open;
          if (next) contactsModalHandlers.open();
          else contactsModalHandlers.close();
        }}
        setAssets={() => {}}
        editingAsset={null}
        clientId={id}
        onContactsCreated={() => {}}
      />
      <BulkImportModal
        descriptor={contactImportDescriptor}
        ctx={{ clientId: id }}
        opened={bulkContactModalOpened}
        onClose={bulkContactModalHandlers.close}
      />
      <BulkImportModal
        descriptor={assetImportDescriptor}
        ctx={{ clientId: id }}
        opened={bulkAssetModalOpened}
        onClose={bulkAssetModalHandlers.close}
      />
      <BulkImportModal
        descriptor={brandImportDescriptor}
        ctx={{ clientId: id }}
        opened={bulkBrandModalOpened}
        onClose={bulkBrandModalHandlers.close}
      />
      <EquipmentFeeModal
        opened={equipmentModalOpened}
        onClose={equipmentModalHandlers.close}
        clientId={id}
      />
    </>
  );
}
